import type { EstimateLine, UsageMeter, WorkspaceBillingState } from '~/features/billing/types'

import { Skeleton } from '~/elements/Skeleton'
import {
  formatCompact,
  formatCount,
  formatDate,
  formatGb,
  formatUsd,
  usagePercent
} from '~/features/billing/utils'
import { cn } from '~/lib/utils'

export type MeterKey = keyof WorkspaceBillingState['usage']

export const METERS: Array<{ key: MeterKey; label: string; format: (value: number | null) => string }> = [
  { key: 'storageGb', label: 'Storage', format: formatGb },
  { key: 'smartSearches', label: 'Smart Searches', format: formatCount },
  { key: 'embeddingTokens', label: 'Embedding tokens', format: formatCompact }
]

const LINE_LABELS: Record<string, string> = {
  storage: 'Storage overage',
  smart_search: 'Smart Search overage',
  smartSearches: 'Smart Search overage',
  embedding_tokens: 'Embedding overage',
  embeddings: 'Embedding overage',
  embeddingTokens: 'Embedding overage'
}

export function meterTone(meter: UsageMeter, hardStop: boolean) {
  const pct = usagePercent(meter.used, meter.included)
  if (meter.overage > 0) return hardStop ? 'danger' : 'over'
  if (meter.included != null && meter.used >= meter.included) return hardStop ? 'danger' : 'warning'
  if (pct >= 90) return 'warning'
  return 'normal'
}

const barColors = {
  normal: 'bg-accent',
  warning: 'bg-warning',
  over: 'bg-badge-blue',
  danger: 'bg-danger'
} as const

export function UsageBar({
  meter,
  hardStop,
  className
}: {
  meter: UsageMeter
  hardStop: boolean
  className?: string
}) {
  const tone = meterTone(meter, hardStop)
  const pct = meter.included == null ? 0 : usagePercent(meter.used, meter.included)

  return (
    <div className={cn('h-1.5 w-full overflow-hidden rounded-full bg-secondary', className)}>
      <div
        className={cn('h-full rounded-full transition-all', barColors[tone])}
        style={{ width: `${tone === 'over' ? 100 : pct}%` }}
      />
    </div>
  )
}

function MeterCard({
  label,
  meter,
  format,
  hardStop
}: {
  label: string
  meter: UsageMeter
  format: (value: number | null) => string
  hardStop: boolean
}) {
  const tone = meterTone(meter, hardStop)
  const pct = usagePercent(meter.used, meter.included)

  return (
    <div className="flex flex-col gap-3 rounded-xl border bg-fill2 p-5">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-medium tracking-wide text-content3 uppercase">{label}</span>
        {meter.included != null && <span className="text-xs text-content3">{pct.toFixed(0)}%</span>}
      </div>
      <p
        className={cn(
          'font-mono text-lg font-bold text-content',
          tone === 'danger' && 'text-danger',
          tone === 'warning' && 'text-warning'
        )}
      >
        {format(meter.used)}
        <span className="text-sm font-medium text-content3"> / {format(meter.included)}</span>
      </p>
      <UsageBar hardStop={hardStop} meter={meter} />
      <p className="text-xs text-content3">
        {meter.overage > 0 ?
          <span className="text-content2">{format(meter.overage)} over the included amount</span>
        : tone === 'danger' ?
          <span className="text-danger">Allowance used up</span>
        : 'Included in your plan'}
      </p>
    </div>
  )
}

export function UsageMeters({
  state,
  loading,
  className
}: {
  state?: WorkspaceBillingState | null
  loading?: boolean
  className?: string
}) {
  if (loading || !state) {
    return (
      <div className={cn('grid grid-cols-1 gap-4 md:grid-cols-3', className)}>
        {METERS.map(({ key }) => (
          <Skeleton className="h-36 w-full rounded-xl" enabled key={key} />
        ))}
      </div>
    )
  }

  const hardStop = state.plan === 'free'

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        {METERS.map(({ key, label, format }) => (
          <MeterCard format={format} hardStop={hardStop} key={key} label={label} meter={state.usage[key]} />
        ))}
      </div>
      <p className="text-xs text-content3">
        Storage is the average stored over the current usage window.
        {state.period ? ` The window resets on ${formatDate(state.period.end)}.` : null}
      </p>
    </div>
  )
}

function EstimateRow({ label, amount, muted }: { label: string; amount: number; muted?: boolean }) {
  return (
    <div className={cn('flex items-center justify-between gap-4 py-2 text-sm', muted && 'text-content3')}>
      <span>{label}</span>
      <span className="font-mono">{formatUsd(amount)}</span>
    </div>
  )
}

const lineLabel = (line: EstimateLine) => LINE_LABELS[line.meter] ?? line.meter.replace(/_/g, ' ')

export function UsageEstimate({ state, className }: { state: WorkspaceBillingState; className?: string }) {
  const { estimate } = state
  const overageLines = estimate.lines.filter((line) => line.amount > 0)

  return (
    <div className={cn('flex flex-col rounded-xl border bg-fill2 p-5', className)}>
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
        <p className="font-semibold text-content">Current window estimate</p>
        {state.period && (
          <span className="text-xs text-content3">
            {formatDate(state.period.start)} – {formatDate(state.period.end)}
          </span>
        )}
      </div>
      <div className="divide-y">
        <EstimateRow amount={estimate.planFee} label="Plan" />
        {estimate.addons > 0 && <EstimateRow amount={estimate.addons} label="Extra seats and projects" />}
        {overageLines.length ?
          overageLines.map((line) => (
            <EstimateRow amount={line.amount} key={line.meter} label={lineLabel(line)} />
          ))
        : <EstimateRow amount={estimate.overage} label="Overage" muted />}
        <div className="flex items-center justify-between gap-4 pt-3 font-semibold text-content">
          <span>Estimated total</span>
          <span className="font-mono text-lg">{formatUsd(estimate.total)}</span>
        </div>
      </div>
      <p className="mt-3 text-xs text-content3">
        Overage is charged once when the window closes. Annual plans still close usage monthly.
      </p>
    </div>
  )
}
